import { useSelector } from "react-redux";
import { ReduxState } from "ra-core";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Typography,
} from "@material-ui/core";
import {
  Button,
  FormDataConsumer,
  SelectInput,
  SimpleForm,
  useDataProvider,
  useNotify,
} from "react-admin";
import DownloadIcon from "@material-ui/icons/GetApp";
import { useFormState } from "react-final-form";
import fileDownload from "js-file-download";
import { Parser } from "json2csv";
import ExportStudentInput from "./ExportStudentInput";
import {
  exportStudentValidation,
  generateExportStudentFilename,
} from "../tools";
import { StudentExportTypeEnum, studentExportTypeList } from "../const";

interface IExportButton {
  setIsLoading?: (isLoading: boolean) => void;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  form: {
    "& .MuiCardContent-root": {
      padding: 0,
    },
  },
  cancelButton: {
    backgroundColor: "#9e9e9e",
  },
  exportTypeInput: {
    marginBottom: "-15px",
  }
});

const ExportButton = ({ setIsLoading }: IExportButton) => {
  const { values } = useFormState();
  const notify = useNotify();
  const { getRecordForCSV } = useDataProvider();

  const [isCSVLoading, setCSVLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [progress, setProgress] = useState<{
    current: number | null;
    total: number | null;
  }>({
    current: 0,
    total: 0,
  });

  const isStudentMasterList = values.exportType === StudentExportTypeEnum.StudentMasterList;
  const studentMasterListValues = {
    isIncludeParent: isStudentMasterList ? true : values.isIncludeParent,
    isIncludeStudentSchedule: isStudentMasterList ? true : values.isIncludeStudentSchedule,
    isIncludeEstimatedRevenue: isStudentMasterList ? "yes" : values.isIncludeEstimatedRevenue,
    isIncludeActiveRoutePlan: isStudentMasterList ? true : values.isIncludeActiveRoutePlan,
  }

  const exportCSV = () => {
    if (isCSVLoading) return;

    const filter: any = {
      SchoolId: values.school.id,
    };

    if (values.isFilterByRegistrationDate && values.startDate) {
      filter.RegisterPeriodStartDate = values.startDate;
    }

    if (values.isFilterByRegistrationDate && values.endDate) {
      filter.RegisterPeriodEndDate = values.endDate;
    }

    if (values.isFilterByStatus && values.status) {
      filter.Status = values.status;
    }

    getRecordForCSV("students", {
      filter,
      body: {
        ...values,
        ...studentMasterListValues
      },
      callback: ({ isLoading, loadedRecordCount, totalRecords }: any) => {
        // Only update loading state once at start and end
        if (isLoading !== undefined) {
          setCSVLoading(isLoading);
          setIsLoading && setIsLoading(isLoading);
        }

        setProgress({
          current: loadedRecordCount,
          total: totalRecords,
        });
      },
    })
      .then(({ data }: any) => {
        const parser = new Parser();

        if (!data || data.length === 0) {
          notify(`No students found for ${values.school.name}`, "warning");
          return;
        }

        const csvData = data.flat();

        let fileName = `Student Master Records - ${values.school.name}.csv`;
        if (values.exportType === StudentExportTypeEnum.Student) {
          fileName = generateExportStudentFilename(values as formExportStudentValues);
        }

        fileDownload(parser.parse(csvData), fileName);
      })
      .catch((e: any) => {
        notify(`${e.message}`, "warning");
      })
      .finally(() => {
        // Reset loading state and progress regardless of success or failure
        setCSVLoading(false);
        setIsLoading && setIsLoading(false);
        setProgress({
          current: null,
          total: null,
        });
      });
  };

  const labelButton = useMemo(() => {
    if (!isCSVLoading) {
      return "Export";
    }

    if (progress.current === null && progress.total === null) {
      return "Fetching Students";
    }

    if (progress.current !== null && progress.total !== null) {
      return `Fetching Data ${progress.current}/${progress.total}`;
    }

    return "Downloading CSV";
  }, [isCSVLoading, progress]);

  useEffect(() => {
    const isFormInvalid = exportStudentValidation({
      ...values,
      ...studentMasterListValues
    } as formExportStudentValues);

    setIsDisabled(isFormInvalid);
  }, [values]);

  return (
    <Button
      onClick={exportCSV}
      label={labelButton}
      color="primary"
      disabled={isDisabled}
      autoFocus
    />
  );
};

const ExportStudentButton = () => {
  const navMode = useSelector((state: ReduxState) => state.navMode);

  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fragment>
      <Button onClick={handleOpenDialog} label="Export">
        <DownloadIcon />
      </Button>

      <SimpleForm
        initialValues={{ school: navMode.school }}
        toolbar={null}
        className={classes.form}
      >
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={classes.dialog}
          maxWidth="md"
        >
          <DialogTitle id="alert-dialog-title">
            Export Students {navMode.school?.name}
          </DialogTitle>

          <DialogContent>
            <DialogContentText color="initial">
              <Typography variant="body1">
                Please select the export type from the dropdown and configure the export options below.
                Toggle each option to include specific student data, or to retrieve data based on filter.
              </Typography>
            </DialogContentText>

            <SelectInput
              className={classes.exportTypeInput}
              source="exportType"
              label="Export Type"
              choices={studentExportTypeList}
              disabled={isLoading}
            />

            <FormDataConsumer>
              {({ formData }) =>
                formData.exportType && (
                  <ExportStudentInput
                    isLoading={isLoading}
                    schoolId={navMode.school.id}
                    isMasterList={formData.exportType === StudentExportTypeEnum.StudentMasterList}
                  />
                )
              }
            </FormDataConsumer>

          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClose}
              color="secondary"
              label="Cancel"
              disabled={isLoading}
              className={classes.cancelButton}
            />
            <ExportButton
              setIsLoading={(value) => {
                setIsLoading(value);
              }}
            />
          </DialogActions>
        </Dialog>
      </SimpleForm>
    </Fragment>
  );
};
export default ExportStudentButton;
